import { permissionsByEntity, useCheckPermissions } from '@cmg/auth';
import { countriesList, UUID } from '@cmg/common';

import { useCheckEntitlements } from '../../../../../common/util/check-entitlements/useCheckEntitlements';
import routeFactory from '../../../../../common/util/routeFactory';
import { ReactRouterDomainNavTabProps } from '../../../../../design-system/react-router/ReactRouterDomainNavTab';
import { Action, OfferingStage, OfferingStatus, Resource } from '../../../../../graphql';

type Props = Readonly<{
  offeringId: UUID | undefined;
  offeringStatus: OfferingStatus | undefined;
  offeringStage: OfferingStage | undefined;
  isAuthor: boolean;
  isInternal?: boolean;
  offeringCountryCode?: string;
}>;

export const useFinalSettlementRoute = ({
  offeringId,
  offeringStatus,
  offeringStage,
  isAuthor,
  isInternal,
  offeringCountryCode,
}: Props): ReactRouterDomainNavTabProps | undefined => {
  const canAccessAnySubmoduleInFS = useCheckPermissions(
    [
      permissionsByEntity.Designation.READ,
      permissionsByEntity.Expense.READ,
      permissionsByEntity.Stabilization.READ,
      permissionsByEntity.Accounting.READ,
      permissionsByEntity.Letter.READ,
    ],
    false
  );
  const { hasEntitlements: canManageFinalSettlement } = useCheckEntitlements({
    offeringId,
    requiredEntitlements: [`${Action.Manage}:${Resource.FinalSettlement}`],
    withPrivateOfferings: false,
  });

  const isPublished = offeringStage === OfferingStage.Published;
  const isPricedTerminatedWithdrawn = [
    OfferingStatus.Priced,
    OfferingStatus.Withdrawn,
    OfferingStatus.Terminated,
  ].includes(offeringStatus!);

  if (!canAccessAnySubmoduleInFS) {
    return undefined;
  }

  if (!offeringId) {
    return undefined;
  }

  if (!isPublished) {
    return undefined;
  }

  if (!isPricedTerminatedWithdrawn) {
    return undefined;
  }

  if (!canManageFinalSettlement) {
    return undefined;
  }

  if (isInternal && !isAuthor) {
    return undefined;
  }

  if (offeringCountryCode !== countriesList.Usa.countryCode) {
    return undefined;
  }

  return {
    href: routeFactory.finalSettlement.getUrlPath({ offeringId }),
    label: 'Final Settlement',
  };
};
